/**
 * O que a pessoa pode fazer **neste contexto** — T18 e T21 da spec #408.
 *
 * A fonte é o manifesto, não a lista que veio do login: a do login é crua, não sabe em que
 * contexto a sessão está e por isso oferecia à empresa o cadastro de EPI, de EPC e a edição de
 * profissional — escrita de catálogo compartilhado, que só a Plataforma grava e que o servidor
 * recusa na hora de gravar. O manifesto já chega estreitado pelo contexto ativo e é refeito a
 * cada troca de contexto, então a resposta muda sem recarregar a página.
 *
 * Enquanto o manifesto não chegou, tudo é `false`. Não há estado de carregamento próprio aqui:
 * `ProtectedRoute` já segura toda tela autenticada em "Carregando seu acesso..." até o
 * manifesto existir, então quem chama este hook já roda com ele em mãos. Um segundo mecanismo
 * só criaria duas respostas para a mesma pergunta.
 *
 * Curinga não é aceito. O catálogo não tem nenhuma permissão com `*`, e um atalho que lê `*`
 * transformaria uma única linha semeada no banco no desligamento de toda a verificação de
 * permissão da interface.
 */
import { useManifesto } from '../stores/manifestoStore';

export const usePermissions = () => {
  const { manifesto } = useManifesto();
  const permissions = manifesto?.permissoes ?? [];

  /** A permissão precisa estar na lista, com o nome literal. */
  const hasPermission = (permission: string): boolean => permissions.includes(permission);

  /**
   * Check if user can perform action on a module
   */
  const can = (action: string, module: string): boolean => {
    return hasPermission(`${module}.${action}`);
  };

  /**
   * Helper methods for common actions
   */
  const canRead = (module: string) => can('read', module);
  const canCreate = (module: string) => can('create', module);
  const canUpdate = (module: string) => can('update', module);
  const canDelete = (module: string) => can('delete', module);
  const canManage = (module: string) => can('manage', module);

  return {
    hasPermission,
    can,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canManage,
    permissions,
  };
};

export default usePermissions;
